import type { Extension, Transaction } from "@codemirror/state";
import {
  Decoration,
  DecorationSet,
  ViewPlugin,
  ViewUpdate,
} from "@codemirror/view";

import {
  aiAssistantStateField,
  setAiAssistantState,
} from "./aiAssistantWidget/aiAssistantWidget";
import {
  StatementGutterMarkerEffect,
  StatementGutterMarkerPendingEffect,
  StatementGutterMarkerState,
} from "./statementGutter/statementGutterMarker";

export function emptyLinePlaceholderExtension(text: string): Extension {
  return ViewPlugin.define(
    view => {
      const makeDecorations = (): DecorationSet => {
        if (!view.hasFocus) return Decoration.none;

        const hasNonEmptySelection = view.state.selection.ranges.some(
          r => !r.empty
        );
        if (hasNonEmptySelection) return Decoration.none;

        const sel = view.state.selection.main;
        const line = view.state.doc.lineAt(sel.head);
        const isEmptyLine = line.text === "";
        if (!isEmptyLine) return Decoration.none;

        // Hide placeholder when AI assistant widget is active
        const internal = view.state.field(aiAssistantStateField, false);
        if (internal && internal.aiState) return Decoration.none;

        const stateObj = view.state.field(StatementGutterMarkerState);
        if (stateObj.pending) return Decoration.none;

        const slice = (stateObj && stateObj.slice) || null;
        const inStatement =
          !!slice && line.from >= slice.pos[0] && line.to <= slice.pos[1];
        if (inStatement) return Decoration.none;

        const deco = Decoration.line({
          attributes: {
            class: "cm-placeholder-line",
            "data-placeholder": text,
          },
        });
        return Decoration.set([deco.range(line.from)]);
      };

      return {
        decorations: makeDecorations(),
        update(update: ViewUpdate) {
          const hasMarkerEffect = update.transactions.some((tr: Transaction) =>
            tr.effects.some(e => e.is(StatementGutterMarkerEffect))
          );
          const hasMarkerPending = update.transactions.some((tr: Transaction) =>
            tr.effects.some(e => e.is(StatementGutterMarkerPendingEffect))
          );
          const hasAiEffect = update.transactions.some((tr: Transaction) =>
            tr.effects.some(e => e.is(setAiAssistantState))
          );

          if (
            update.docChanged ||
            update.selectionSet ||
            update.focusChanged ||
            hasMarkerEffect ||
            hasMarkerPending ||
            hasAiEffect
          ) {
            this.decorations = makeDecorations();
          }
        },
      };
    },
    {
      decorations: v => v.decorations,
    }
  );
}
